import { supabase } from './supabaseClient';

export const STAFF_ASSIGNMENT_TEMPLATE_VARIABLES = [
  'recipient_name', 'event_name', 'event_date', 'crew_call_time',
  'venue', 'crew_portal_url', 'event_reference'
] as const;

export type StaffAssignmentConfig = {
  enabled: boolean;
  channel: 'EMAIL' | 'SMS' | 'BOTH';
  follow_up_enabled: boolean;
  follow_up_delay_hours: number;
  email_subject: string;
  email_body: string;
  sms_body: string;
};

export type LoadedStaffAssignmentConfig = StaffAssignmentConfig & {
  id: string;
  version: number;
  created_at: string;
};

export const DEFAULT_STAFF_ASSIGNMENT_CONFIG: StaffAssignmentConfig = {
  enabled: false,
  channel: 'EMAIL',
  follow_up_enabled: true,
  follow_up_delay_hours: 24,
  email_subject: 'Crew assignment — {{event_name}} on {{event_date}}',
  email_body: `Hi {{recipient_name}},

You have a new QuikPix crew assignment that needs your response.

Event: {{event_name}}
Date: {{event_date}}
Your Active Shift Crew time: {{crew_call_time}}
Venue: {{venue}}

Accept or decline in the Crew Portal:
{{crew_portal_url}}

Reference: {{event_reference}}`,
  sms_body: 'QuikPix assignment: {{event_name}} on {{event_date}}, crew time {{crew_call_time}}. Please accept or decline: {{crew_portal_url}}'
};

const variablePattern = /\{\{\s*([a-z_]+)\s*\}\}/g;

export function unknownStaffAssignmentVariables(value: string): string[] {
  const names = Array.from(value.matchAll(variablePattern), match => match[1]);
  const unknown = names.filter(name => !(STAFF_ASSIGNMENT_TEMPLATE_VARIABLES as readonly string[]).includes(name));
  const remainder = value.replace(variablePattern, '');
  if (remainder.includes('{{') || remainder.includes('}}')) unknown.push('malformed variable');
  return Array.from(new Set(unknown));
}

export function validateStaffAssignmentConfig(config: StaffAssignmentConfig): string[] {
  const errors: string[] = [];
  if (!['EMAIL', 'SMS', 'BOTH'].includes(config.channel)) errors.push('Select Email, SMS, or Both.');
  if (!Number.isInteger(config.follow_up_delay_hours) || config.follow_up_delay_hours < 1 || config.follow_up_delay_hours > 168) {
    errors.push('Follow-up delay must be between 1 and 168 hours.');
  }
  if (!config.email_subject.trim() || config.email_subject.length > 250 || /[\r\n]/.test(config.email_subject)) {
    errors.push('Email subject must be one line and 250 characters or fewer.');
  }
  if (!config.email_body.trim() || config.email_body.length > 20000) errors.push('Email body is required.');
  if (!config.sms_body.trim() || config.sms_body.length > 1600) errors.push('SMS body is required and must be 1,600 characters or fewer.');
  const unknown = [config.email_subject, config.email_body, config.sms_body].flatMap(unknownStaffAssignmentVariables);
  if (unknown.length) errors.push(`Unknown template variable: ${Array.from(new Set(unknown)).join(', ')}.`);
  if (!config.email_body.includes('{{crew_call_time}}') || !config.sms_body.includes('{{crew_call_time}}')) {
    errors.push('Both templates must include the individual Active Shift Crew time.');
  }
  if (!config.email_body.includes('{{crew_portal_url}}') || !config.sms_body.includes('{{crew_portal_url}}')) {
    errors.push('Both templates must include the Crew Portal link.');
  }
  return errors;
}

export const cloneStaffAssignmentConfig = (config: StaffAssignmentConfig): StaffAssignmentConfig => ({ ...config });

export async function loadStaffAssignmentConfig(): Promise<LoadedStaffAssignmentConfig | null> {
  const { data, error } = await supabase.rpc('admin_staff_assignment_config');
  if (error) throw new Error(error.message);
  return data ? data as LoadedStaffAssignmentConfig : null;
}

export async function saveStaffAssignmentConfig(expectedVersion: number, config: StaffAssignmentConfig) {
  const errors = validateStaffAssignmentConfig(config);
  if (errors.length) throw new Error(errors.join(' '));
  const { data, error } = await supabase.rpc('admin_save_staff_assignment_config', {
    p_expected_version: expectedVersion,
    p_config: config
  });
  if (error) throw new Error(error.message);
  return data as { id: string; version: number; enabled: boolean };
}
